export type SvgIntrinsicGeometry = {
  width: number
  height: number
  viewBox: { x: number; y: number; width: number; height: number }
  aspectRatio: number
}

const VIEWBOX_RE = /^\s*(-?[\d.]+)\s*[,\s]\s*(-?[\d.]+)\s*[,\s]\s*([\d.]+)\s*[,\s]\s*([\d.]+)\s*$/

export const parseViewBox = (
  raw: string | null | undefined,
): { x: number; y: number; width: number; height: number } | null => {
  if (!raw) return null
  const m = VIEWBOX_RE.exec(raw)
  if (!m) return null
  const width = parseFloat(m[3])
  const height = parseFloat(m[4])
  if (width <= 0 || height <= 0) return null
  return { x: parseFloat(m[1]), y: parseFloat(m[2]), width, height }
}

const parseNumericAttr = (value: string | null | undefined): number | null => {
  if (!value) return null
  const trimmed = value.trim()
  if (trimmed.endsWith("%")) return null
  const num = parseFloat(trimmed)
  return Number.isFinite(num) && num > 0 ? num : null
}

export const extractSvgGeometry = (svgElement: SVGSVGElement): SvgIntrinsicGeometry | null => {
  const vbAttr = svgElement.getAttribute("viewBox")
  const vb = parseViewBox(vbAttr)

  const widthAttr = parseNumericAttr(svgElement.getAttribute("width"))
  const heightAttr = parseNumericAttr(svgElement.getAttribute("height"))

  let width: number
  let height: number
  let viewBox: { x: number; y: number; width: number; height: number }

  if (vb) {
    viewBox = vb
    width = widthAttr ?? vb.width
    height = heightAttr ?? vb.height
  } else if (widthAttr && heightAttr) {
    width = widthAttr
    height = heightAttr
    viewBox = { x: 0, y: 0, width, height }
  } else {
    return null
  }

  return {
    width,
    height,
    viewBox,
    aspectRatio: width / height,
  }
}

export const extractSvgGeometryFromString = (svgString: string): SvgIntrinsicGeometry | null => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(svgString, "image/svg+xml")
  const svgElement = doc.querySelector("svg")
  if (!svgElement) return null
  return extractSvgGeometry(svgElement as unknown as SVGSVGElement)
}
